import React from 'react';
import PropTypes from 'prop-types';
import MuiAlert from '@material-ui/lab/Alert';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { FormattedMessage } from 'react-intl';
import { statuses } from 'constants/booking';
import moment from 'logic/moment';

const MESSAGES = {
  [statuses.PENDING]: (booking) => ({
    severity: 'info',
    title: (
      <FormattedMessage
        id="pending"
        defaultMessage="Pending"
      />
    ),
    content: (
      <FormattedMessage
        id="bookinginfo.status.expiration"
        defaultMessage="Expires on {date}"
        values={{
          date: moment(booking.booking_expiration_date).format('LLL'),
        }}
      />
    ),
  }),
  [statuses.CANCELLED]: (booking) => {
    const by = booking.Guest.id === booking.Cancellation.user_id
      ? booking.Guest
      : booking.Host;

    return {
      severity: 'error',
      title: (
        <FormattedMessage
          id="cancelled"
          defaultMessage="Cancelled"
        />
      ),
      content: (
        <FormattedMessage
          id="bookinginfo.status.cancelled"
          defaultMessage="Cancelled on {date} by {user}"
          values={{
            date: moment(booking.cancelled_at).format('LLL'),
            user: by.name,
          }}
        />
      ),
    };
  },
  [statuses.PAID]: (booking) => ({
    severity: 'success',
    title: (
      <FormattedMessage
        id="paid"
        defaultMessage="Paid"
      />
    ),
    content: (
      <FormattedMessage
        id="bookinginfo.status.paid"
        defaultMessage="Paid on {date}"
        values={{
          date: moment(booking.paid_at).format('LLL'),
        }}
      />
    ),
  }),
  [statuses.EXPIRED]: (booking) => ({
    severity: 'error',
    title: (
      <FormattedMessage
        id="expired"
        defaultMessage="Expired"
      />
    ),
    content: (
      <FormattedMessage
        id="bookinginfo.status.expired"
        defaultMessage="Expired on {date}"
        values={{
          date: moment(booking.booking_expiration_date).format('LLL'),
        }}
      />
    ),
  }),
  [statuses.FINISHED]: (booking) => ({
    severity: 'success',
    title: (
      <FormattedMessage
        id="finished"
        defaultMessage="Finished"
      />
    ),
    content: (
      <FormattedMessage
        id="bookinginfo.status.finished"
        defaultMessage="Finished on {date}"
        values={{
          date: moment(booking.finished_at).format('LLL'),
        }}
      />
    ),
  }),
};

const Alert = ({ severity, title, content }) => (
  <MuiAlert severity={severity} variant="filled">
    <Typography style={{ textTransform: 'uppercase' }}>
      {title}
    </Typography>
    {content}
  </MuiAlert>
);

Alert.propTypes = {
  severity: 'ropTypes.string.isRequire',
  title: PropTypes.node.isRequired,
  content: PropTypes.node.isRequired,
};

const Content = ({ booking }) => {
  if (!MESSAGES[booking.status]) {
    return null;
  }

  const { severity, title, content } = MESSAGES[booking.status](booking);

  return (
    <MuiAlert severity={severity} variant="filled">
      <Typography style={{ textTransform: 'uppercase' }}>
        {title}
      </Typography>
      {content}
    </MuiAlert>
  );
};

Content.propTypes = {
  booking: PropTypes.shape({
    status: PropTypes.string,
  }),
};

const PrintStatus = ({ booking }) => {
  if (!MESSAGES[booking.status]) {
    return null;
  }

  const { content } = MESSAGES[booking.status](booking);

  return (
    <Box>
      {content}
    </Box>
  );
};

PrintStatus.propTypes = {
  booking: PropTypes.shape({
    status: PropTypes.string,
  }),
};

const Status = ({ booking }) => {
  const printing = useMediaQuery('@media print');

  if (printing) {
    return <PrintStatus booking={booking} />;
  }

  return (
    <Box mt={1} textAlign="left">
      <Content booking={booking} />
    </Box>
  );
};

Status.propTypes = {
  booking: PropTypes.shape({}),
};

export default Status;
